/**
 * Portal training hooks -- recipient-facing enrollment + SCORM launch.
 *
 * Portal API calls use the portal JWT (separate from the admin JWT).
 * Token stored as `hf-portal:token` via portalAuthApi helpers.
 *
 * BE endpoints assumed under `/v1/portal/enrollments/*`.
 * If BE agent (P1-D) uses a different prefix, update PORTAL_PREFIX below.
 */
import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_CONFIG } from '@/lib/api/config';
import { portalAuthApi } from '@/lib/api/portal-auth';
import type { ReattemptPolicy } from '@/lib/lms/schemas';

// ============================================================================
// Portal Fetch Wrapper
// ============================================================================

const PORTAL_PREFIX = '/portal/enrollments';

/**
 * Thin fetch wrapper that reads the portal JWT from the shared portalAuthApi.
 * Does NOT use fetchApi (which injects admin auth headers + workspace context).
 */
async function portalFetch<T>(
  path: string,
  opts?: RequestInit,
): Promise<T> {
  const url = `${API_CONFIG.baseUrl}${API_CONFIG.versionPrefix}${path}`;
  const buildInit = (tokenOverride?: string | null): RequestInit => {
    const t = tokenOverride ?? portalAuthApi.getToken();
    return {
      ...opts,
      headers: {
        'Content-Type': 'application/json',
        ...(t ? { Authorization: `Bearer ${t}` } : {}),
        ...(opts?.headers as Record<string, string> | undefined),
      },
    };
  };

  // 2026-05-22 — refresh-on-401 retry path. Pre-fix, an expired access
  // token (most common after the recipient leaves the tab open for >1h)
  // would throw `Error("401: …")` here, the React Query hook would
  // surface "Course Not Available", and the user was stuck despite
  // having a valid refresh token sitting in localStorage. Now we mirror
  // `portalAuthApi.authedFetch` — on 401, attempt one refresh, retry
  // once with the new access token. On refresh failure, throw the
  // original 401 so the SCORM player's error-state branch still renders.
  let res = await fetch(url, buildInit());
  if (res.status === 401) {
    const refreshed = await portalAuthApi.refresh();
    if (refreshed) {
      res = await fetch(url, buildInit(refreshed));
    }
  }

  if (!res.ok) {
    const body = await res.text().catch(() => 'Unknown error');
    throw new Error(`${res.status}: ${body}`);
  }

  return res.json() as Promise<T>;
}

// ============================================================================
// Types
// ============================================================================

export interface PortalEnrollment {
  id: string;
  courseId: string;
  courseName: string;
  courseDescription: string;
  /** Thumbnail image — may be empty string (portal FE falls back to
   *  a GraduationCap icon); BE emits `courseThumbnailUrl` from the
   *  courses.thumbnail_url column. */
  courseThumbnailUrl: string;
  /** Integer minutes; portal FE renders as "{n} min". */
  courseDurationMinutes: number;
  status: string; // 'Not Started' | 'In Progress' | 'Completed' | 'Passed' | 'Failed'
  assignedAt: string;
  completedAt: string;
  /** Canonical BE field name (0-100). */
  progressPercent: number;
  /** Back-compat alias — older components read `.progress`. Defaults to
   *  `progressPercent` when missing. BE may omit; keep optional. */
  progress?: number;
  dueDate: string;
  // Reattempt policy fields — added 2026-04-21 (spec: course-attempt-reattempt-policy)
  reattemptPolicy?: ReattemptPolicy;
  reattemptCooldownHours?: number;
  attemptsCount?: number;
  lastAttemptedAt?: string | null;
  firstPassedAt?: string | null;
  grantRetriesGranted?: number;
  grantRetriesUsed?: number;
}

export interface ScormLaunchData {
  launchUrl: string;
  token: string;
  enrollmentId: string;
  courseId: string;
  /** course_content.id — required by the SCORM serve proxy URL builder
   *  (Bug fix 2026-05-22, see usePortalScormPlayToken). The BE has always
   *  returned this in the response; the FE type was just missing it. */
  contentId: string;
  /** SCORM 1.2 / 2004 — used by the SCORM API shim on the iframe side. */
  scormVersion?: string;
  /** Display only, surfaced in the player chrome. */
  courseName?: string;
}

// ============================================================================
// Query Key Factory
// ============================================================================

export const portalTrainingKeys = {
  all: ['portal'] as const,
  enrollments: {
    all: () => [...portalTrainingKeys.all, 'enrollments'] as const,
    list: () => [...portalTrainingKeys.enrollments.all(), 'list'] as const,
    detail: (id: string) =>
      [...portalTrainingKeys.enrollments.all(), 'detail', id] as const,
  },
  scormLaunch: (id: string) =>
    [...portalTrainingKeys.all, 'scorm-launch', id] as const,
  // FIX #668 #673: dashboard aggregates simulation stats + riskStatus
  dashboard: () => [...portalTrainingKeys.all, 'dashboard'] as const,
};

// ============================================================================
// Queries
// ============================================================================

/**
 * Fetch the recipient's list of assigned enrollments.
 * BE returns `{ data: PortalEnrollment[] }` envelope.
 */
export function usePortalEnrollments() {
  return useQuery({
    queryKey: portalTrainingKeys.enrollments.list(),
    queryFn: () =>
      portalFetch<{ data: PortalEnrollment[] }>(PORTAL_PREFIX),
    placeholderData: keepPreviousData,
    // Normalize progress — BE emits `progressPercent` (canonical) but some
    // legacy consumers read `.progress`. Mirror into both so older cards
    // still render without refactoring.
    select: (res) =>
      res.data.map((e) => ({
        ...e,
        progress: e.progress ?? e.progressPercent ?? 0,
      })),
  });
}

/**
 * Fetch a single enrollment by id.
 */
export function usePortalEnrollment(enrollmentId: string) {
  return useQuery({
    queryKey: portalTrainingKeys.enrollments.detail(enrollmentId),
    queryFn: () =>
      portalFetch<PortalEnrollment>(`${PORTAL_PREFIX}/${enrollmentId}`),
    enabled: !!enrollmentId,
  });
}

/**
 * Fetch SCORM launch data for an enrollment.
 * Returns the iframe URL + play token.
 */
export function useScormLaunch(enrollmentId: string) {
  return useQuery({
    queryKey: portalTrainingKeys.scormLaunch(enrollmentId),
    queryFn: () =>
      portalFetch<ScormLaunchData>(
        `${PORTAL_PREFIX}/${enrollmentId}/scorm-launch`,
      ),
    enabled: !!enrollmentId,
  });
}

/**
 * Mint a public HMAC-signed launch link for the canonical `/player/<courseId>`
 * page (the same page recipients land on from the post-compromise training
 * email).
 *
 * Returns `{playerUrl, expiresAt}` where playerUrl is shaped like:
 *   `${FRONTEND_URL}/player/<courseId>?e=<enrollmentId>&k=<hmac>`
 *
 * Architectural note (2026-05-22): the portal "My Training" shelf used to
 * embed its own SCORM iframe at `/portal/training/<enrollmentId>`. That
 * duplicated the SCORM-rendering surface already implemented in
 * `app/player/[courseId]/page.tsx` (the canonical player used by the
 * post-compromise training email link). Two surfaces meant 2x maintenance +
 * the portal one was a half-finished stub. We now collapse both UX paths
 * onto the single `/player/[courseId]` surface — portal table rows mint
 * this URL on click and open it in a new tab.
 */
export function useGeneratePortalLaunchLink() {
  return useMutation({
    mutationFn: (enrollmentId: string) =>
      portalFetch<{ playerUrl: string; expiresAt: string }>(
        `${PORTAL_PREFIX}/${enrollmentId}/launch-link`,
        { method: 'POST' },
      ),
  });
}

// ============================================================================
// Mutations
// ============================================================================

/**
 * Post SCORM CMI progress data back to the backend.
 * Called by the iframe postMessage bridge.
 */
export function useUpdateScormProgress() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      enrollmentId,
      cmiData,
    }: {
      enrollmentId: string;
      cmiData: Record<string, string>;
    }) =>
      portalFetch<{ success: boolean }>(
        `${PORTAL_PREFIX}/${enrollmentId}/scorm-progress`,
        {
          method: 'POST',
          body: JSON.stringify(cmiData),
        },
      ),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({
        queryKey: portalTrainingKeys.enrollments.detail(vars.enrollmentId),
      });
      void qc.invalidateQueries({
        queryKey: portalTrainingKeys.enrollments.list(),
      });
    },
  });
}

// ============================================================================
// Portal Dashboard (aggregated stats)
// FIX #668 #673: fetch real simulation + training + risk data from the portal
// dashboard endpoint instead of relying on synthetic fallback values.
// ============================================================================

/** Shape returned by GET /v1/portal/dashboard */
export interface PortalDashboardStats {
  trainingStats: {
    totalAssigned: number;
    totalCompleted: number;
    totalInProgress: number;
    totalPassed: number;
    completionRate: number;
  };
  simulationStats: {
    totalReceived: number;
    totalClicked: number;
    totalReported: number;
    totalCompromised: number;
    reportRate: number;
  };
  /** Risk status label from recipients table (None/Safe/Low/Medium/High/Very High). */
  riskStatus: string;
  totalPoints: number;
  currentStreak: number;
  longestStreak: number;
}

/**
 * Fetch the portal dashboard summary (simulation stats, training stats,
 * risk status) for the authenticated recipient.
 *
 * #668: provides real `simulationStats.totalReceived` → Sent count.
 * #673: provides real `riskStatus` → portal Risk Score widget.
 */
export function usePortalDashboard() {
  return useQuery({
    queryKey: portalTrainingKeys.dashboard(),
    queryFn: () =>
      portalFetch<PortalDashboardStats>('/portal/dashboard'),
    placeholderData: keepPreviousData,
  });
}
